/**
 * RTK Query Base Query with Automatic Token Refresh
 *
 * Features:
 * - Intercepts 401 errors
 * - Automatically refreshes access token using refresh token
 * - Retries failed request with new token
 * - Refresh is shared and single-flight (refreshSession)
 * - Logs out user only when the refresh token is rejected (401); keeps the session on transient errors
 */

import {
  BaseQueryFn,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';
import type { RootState } from '../App/store';
import { logout } from '../Features/Auth/slices/authSlice';
import { toast } from 'react-toastify';
import { refreshSession } from '../Features/Auth/services/refreshSession';
import { resolveAdminApiUrl } from './apiUrl';

// Same id as the toast of useTokenRefresh, so both paths show at most one
const SESSION_EXPIRED_TOAST_ID = 'auth-session-expired';
const ADMIN_API_URL = resolveAdminApiUrl(process.env.REACT_APP_API_URL);

const parseJsonOrText = async (response: Response): Promise<unknown> => {
  // RTK Query default is JSON parsing; backend may return plain text for 500/403/404.
  // Returning text avoids PARSING_ERROR while still preserving the error message.
  if (response.status === 204) {
    return null;
  }

  const contentType = response.headers.get('content-type') ?? '';
  const rawBody = await response.text();

  if (!rawBody) {
    return null;
  }

  const looksLikeJson =
    contentType.includes('application/json') ||
    contentType.includes('application/problem+json') ||
    rawBody.trim().startsWith('{') ||
    rawBody.trim().startsWith('[');

  if (!looksLikeJson) {
    return rawBody;
  }

  try {
    return JSON.parse(rawBody);
  } catch {
    return rawBody;
  }
};

/**
 * Base query with automatic token refresh on 401 errors
 */
export const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const requestMethod = typeof args === 'string' ? 'GET' : (args.method ?? 'GET');
  const requestUrl = typeof args === 'string' ? args : args.url;
  const isDeleteRequest = requestMethod.toUpperCase() === 'DELETE';

  if (isDeleteRequest) {
    console.log('[API][DELETE] Request start', {
      method: requestMethod,
      url: `${ADMIN_API_URL}${requestUrl}`,
      args,
    });
  }

  // Create base query with JWT token
  const adminBaseQuery = fetchBaseQuery({
    baseUrl: ADMIN_API_URL,
    responseHandler: parseJsonOrText,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token;
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }

      if (!headers.has('accept')) {
        headers.set('accept', 'application/json');
      }
      return headers;
    },
  });

  const tokenAtRequestStart = (api.getState() as RootState).auth.token;

  // Execute initial request
  let result = await adminBaseQuery(args, api, extraOptions);

  // Handle 401 Unauthorized - token expired
  // A 401 after a manual logout is not retried: no refresh, no second logout, no toast
  if (
    result.error &&
    result.error.status === 401 &&
    (api.getState() as RootState).auth.isAuthenticated
  ) {
    const currentToken = (api.getState() as RootState).auth.token;

    if (currentToken && currentToken !== tokenAtRequestStart) {
      // Another caller already refreshed the session while this request was in flight
      result = await adminBaseQuery(args, api, extraOptions);
    } else {
      const refreshOutcome = await refreshSession({
        dispatch: api.dispatch,
        getState: api.getState,
      });

      if (refreshOutcome === 'success') {
        // Retry original request with new token
        result = await adminBaseQuery(args, api, extraOptions);
      } else if (
        refreshOutcome === 'unauthorized' &&
        (api.getState() as RootState).auth.isAuthenticated
      ) {
        // Parallel callers share one refresh: only the first one still sees an authenticated store
        api.dispatch(logout());
        toast.error('Session expired. Please log in again.', {
          toastId: SESSION_EXPIRED_TOAST_ID,
          position: 'top-right',
          autoClose: 5000,
        });
      }
      // transient or stale: keep the session, the original 401 result is returned to the caller
    }
  }

  if (isDeleteRequest) {
    if (result.error) {
      console.error('[API][DELETE] Request failed', {
        method: requestMethod,
        url: `${ADMIN_API_URL}${requestUrl}`,
        status: result.error.status,
        error: result.error,
      });
    } else {
      console.log('[API][DELETE] Request success', {
        method: requestMethod,
        url: `${ADMIN_API_URL}${requestUrl}`,
        data: result.data,
      });
    }
  }

  if (result.error) {
    const errorStatus = result.error.status;
    const isServerError = typeof errorStatus === 'number' && errorStatus >= 500;
    const isTransportError =
      errorStatus === 'FETCH_ERROR' ||
      errorStatus === 'PARSING_ERROR' ||
      errorStatus === 'TIMEOUT_ERROR';

    if (isServerError || isTransportError) {
      console.error('[API] Request failed with server/transport error', {
        method: requestMethod,
        url: `${ADMIN_API_URL}${requestUrl}`,
        status: errorStatus,
        errorData: result.error.data,
        originalArgs: args,
      });
    }
  }

  return result;
};
